import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import { Tenant, syncTenantAuth, getKeycloakStatus, reconcileTenant, testKeycloakConnection, syncJwksUrls, updateSessionPolicy, syncSessionTimeouts, forceLogoutUser, SessionPolicy, KcSessionTimeouts, defaultSessionPolicy, KcSyncCheckResult, KcOverallStatus, KcConnectionTestResult, JwksSyncResult, updateSecuritySettings, SecuritySettings, defaultSecuritySettings, VALID_ALGORITHMS, updateLoginMethods, runTenantDiagnostics, TenantDiagnosticsResult, initializeAllKeys, updateDedicatedDeploymentObjectTypes, updateSubtenantsEnabled, updateSignatureProviderSettings, SignatureProviderSettings, defaultSignatureProviderSettings, defaultSkribbleSettings, defaultDocuSignSettings, SIGNATURE_PROVIDERS } from '../api/tenantApi';
import {
    fetchTenantById,
    updateTenant,
    deleteTenant,
} from '../store/slices/tenantSlice';
import { addNotification, showModal } from '../store/slices/uiSlice';
import * as Yup from 'yup';
import {
    FiArrowLeft,
    FiEdit2,
    FiSave,
    FiX,
    FiCopy,
    FiRefreshCw,
    FiTrash2,
    FiZap,
    FiPlus,
    FiKey,
    FiRotateCw,
    FiExternalLink,
} from 'react-icons/fi';
import { fetchClusters } from '../store/slices/clusterSlice';
import TenantStatusManager from '../components/tenant/TenantStatusManager';
import TenantReadinessPanel from '../components/tenant/TenantReadinessPanel';
import * as encKeyApi from '../api/encryptionKeyManagementApi';
import type { EncryptionKeyStatus } from '../api/encryptionKeyManagementApi';

// Define types for clusters
interface Cluster {
    _id: string;
    clusterId: string;
}

// Extend Tenant interface for our specific needs
/*interface ExtendedTenant extends Tenant {
    createK8sIngress?: boolean;
    url?: string;
}*/

// Validation schema — authenticationProviders excluded: they are built from
// the auth-method checkboxes in handleUpdateTenant, not edited as raw form fields.
const TenantSchema = Yup.object().shape({
    tenantId: Yup.string()
        .required('Tenant ID is required')
        .matches(/^[a-zA-Z0-9-_]+$/, 'Only alphanumeric characters, hyphens, and underscores are allowed'),
    name: Yup.string()
        .required('Name is required'),
    url: Yup.string()
        .required('URL is required'),
    databaseName: Yup.string().required('Database name is required'),
    cluster: Yup.string().required('Cluster is required'),
});

// Shape returned by POST /tenants/:id/keycloak-reconcile. Declared locally so the reconcile
// report can be rendered without widening the shared tenantApi surface.
interface ReconcileStep {
    step: string;
    ok: boolean;
    fatal?: boolean;
    skipped?: boolean;
    error?: string;
}
interface ReconcileResponse {
    success: boolean;
    status?: 'ok' | 'partial' | 'failed';
    message?: string;
    kcCheck?: KcSyncCheckResult;
    steps?: ReconcileStep[];
    failedSteps?: ReconcileStep[];
}

const TenantDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { selectedTenant, loadingTenant, error } = useSelector((state: RootState) => state.tenants);
    const [isEditing, setIsEditing] = useState(false);
    const [showStatusManager, setShowStatusManager] = useState(false);
    const [syncRetryLoading, setSyncRetryLoading] = useState(false);
    const [kcStatus, setKcStatus] = useState<KcSyncCheckResult | null>(null);
    const [kcStatusLoading, setKcStatusLoading] = useState(false);
    const [kcReconcileLoading, setKcReconcileLoading] = useState(false);
    // Per-step outcome of the last Reconcile. syncAuthMethods isolates each step, so a
    // reconcile can now half-succeed; without this the UI would show a green toast while
    // eight steps had silently been skipped (exactly what hid the varchar(255) bug).
    const [kcSteps, setKcSteps] = useState<ReconcileStep[] | null>(null);
    const [kcTestLoading, setKcTestLoading] = useState(false);
    const [kcTestResult, setKcTestResult] = useState<KcConnectionTestResult | null>(null);
    const [jwksSyncLoading, setJwksSyncLoading] = useState(false);
    const [jwksSyncResult, setJwksSyncResult] = useState<JwksSyncResult | null>(null);
    const [sessionPolicyEditing, setSessionPolicyEditing] = useState(false);
    const [sessionPolicySaving, setSessionPolicySaving] = useState(false);
    const [sessionPolicyDraft, setSessionPolicyDraft] = useState<SessionPolicy | null>(null);
    const [forceLogoutUserId, setForceLogoutUserId] = useState('');
    const [forceLogoutLoading, setForceLogoutLoading] = useState(false);
    const [kcTimeoutsDraft, setKcTimeoutsDraft] = useState<Partial<KcSessionTimeouts>>({});
    const [kcTimeoutsSaving, setKcTimeoutsSaving] = useState(false);
    const [securityEditing, setSecurityEditing] = useState(false);
    const [securitySaving, setSecuritySaving] = useState(false);
    const [securityDraft, setSecurityDraft] = useState<SecuritySettings | null>(null);
    const [signatureProviderEditing, setSignatureProviderEditing] = useState(false);
    const [signatureProviderSaving, setSignatureProviderSaving] = useState(false);
    const [signatureProviderDraft, setSignatureProviderDraft] = useState<SignatureProviderSettings | null>(null);
    const [loginMethodSaving, setLoginMethodSaving] = useState(false);
    const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
    const [diagnosticsResult, setDiagnosticsResult] = useState<TenantDiagnosticsResult | null>(null);
    const [initKeysLoading, setInitKeysLoading] = useState(false);
    const [dedicatedTypesEditing, setDedicatedTypesEditing] = useState(false);
    const [dedicatedTypesSaving, setDedicatedTypesSaving] = useState(false);
    const [dedicatedTypesDraft, setDedicatedTypesDraft] = useState<string[] | null>(null);
    const [dedicatedTypesNewEntry, setDedicatedTypesNewEntry] = useState('');
    const [subtenantsSaving, setSubtenantsSaving] = useState(false);

    // ── Encryption key state ──────────────────────────────────────────────────
    const [encKeyStatus, setEncKeyStatus] = useState<EncryptionKeyStatus | null>(null);
    const [encKeyStatusLoading, setEncKeyStatusLoading] = useState(false);
    const [encKeyStatusError, setEncKeyStatusError] = useState<string | null>(null);
    const [encKeyInitLoading, setEncKeyInitLoading] = useState(false);
    const [encKeyRotateLoading, setEncKeyRotateLoading] = useState(false);
    const [showRotateConfirm, setShowRotateConfirm] = useState(false);

    useEffect(() => {
        if (id) {
            dispatch(fetchTenantById(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(fetchClusters());
    }, [dispatch]);

    // Background KC status check — fires once when the tenant is loaded
    useEffect(() => {
        if (!id || !selectedTenant) return;
        // Seed with stored value immediately while live check runs
        const stored = (selectedTenant as any).keycloak?.sync?.kcCheck;
        if (stored) setKcStatus(stored);
        // Live check in background
        setKcStatusLoading(true);
        getKeycloakStatus(id)
            .then(r => { if (r.data) setKcStatus(r.data); })
            .catch(() => {})
            .finally(() => setKcStatusLoading(false));
    }, [id, selectedTenant?.tenantId]); // re-check when tenant ID changes, not on every re-render

    // Background encryption key status check — fires once when the tenant is loaded
    useEffect(() => {
        if (!selectedTenant?.tenantId) return;
        setEncKeyStatusLoading(true);
        setEncKeyStatusError(null);
        encKeyApi.getEncryptionKeyStatus(selectedTenant.tenantId)
            .then(r => { if (r?.data) setEncKeyStatus(r.data); })
            .catch((err: any) => {
                const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to load encryption key status.';
                setEncKeyStatusError(msg);
            })
            .finally(() => setEncKeyStatusLoading(false));
    }, [selectedTenant?.tenantId]);

    const handleUpdateTenant = async (values: any) => {
        if (!id) return;

        try {
            // Keep existing credential provider, replace oidc entries with what's in the form
            const existingCredential = (selectedTenant?.authenticationProviders ?? []).find((p: any) => p.type === 'credential')
                ?? { type: 'credential', providerId: 'credential', label: 'Sign in credential', fields: { password: { label: 'Password', type: 'Password' } } };

            const authenticationProviders = [
                existingCredential,
                ...(values.oidcProviders ?? []).filter((p: any) => p.label || p.clientId),
            ];

            // Only send real document fields — no form-only helpers (authOtp, oidcProviders, etc.)
            const updateData: Record<string, any> = {
                url: values.url,                    // server maps → baseUrl
                name: values.name,
                databaseName: values.databaseName,
                cluster: values.cluster,
                secretStoreName: values.secretStoreName,
                authenticationProviders,
                keys: values.keys,
                email: values.email,
                provisioning: {
                    internal: { mode: values.provisioningInternalMode },
                    external: { mode: values.provisioningExternalMode },
                },
                redis: {
                    username: values.redisUsername,
                    passwordSecretKey: values.redisPasswordKey,
                },
                // authMethods triggers KC sync but is stripped before MongoDB save
                authMethods: {
                    credential: true,
                    otp: values.authOtp,
                    magicLink: values.authMagicLink,
                    federatedAd: null,
                },
            };
            // KC infrastructure fields — stored as dot-notation so MongoDB updates only these sub-fields
            if (values.kcBaseUrl      !== undefined) updateData['keycloak.baseUrl']      = values.kcBaseUrl      || null;
            if (values.kcJwksBaseUrl  !== undefined) updateData['keycloak.jwksBaseUrl']  = values.kcJwksBaseUrl  || null;
            if (values.kcAppClientId  !== undefined) updateData['keycloak.appClientId']  = values.kcAppClientId  || null;
            if (values.kcClientSecret !== undefined) updateData['keycloak.clientSecret'] = values.kcClientSecret || null;
            if (values.kcRedirectUri  !== undefined) updateData['keycloak.redirectUri']  = values.kcRedirectUri  || null;

            await dispatch(updateTenant({ id, updateData })).unwrap();

            dispatch(
                addNotification({
                    type: 'success',
                    message: `Tenant "${values.tenantId}" was successfully updated.`,
                })
            );

            setIsEditing(false);
        } catch (error: any) {
            dispatch(
                addNotification({
                    type: 'error',
                    title: 'Update Failed',
                    message: error.message || 'Failed to update tenant.',
                })
            );
        }
    };

    const handleDelete = () => {
        if (!id || !selectedTenant) return;

        dispatch(
            showModal({
                title: 'Confirm Deletion',
                content: `Are you sure you want to delete tenant "${selectedTenant.tenantId}"? The tenant will be moved to the deleted tenants list and can be restored later.`,
                confirmLabel: 'Delete',
                cancelLabel: 'Cancel',
                onConfirm: async () => {
                    try {
                        await dispatch(deleteTenant(id)).unwrap();

                        dispatch(
                            addNotification({
                                type: 'success',
                                message: `Tenant "${selectedTenant.tenantId}" was successfully deleted.`,
                            })
                        );
                        navigate('/tenants');
                    } catch (error: any) {
                        dispatch(
                            addNotification({
                                type: 'error',
                                title: 'Deletion Failed',
                                message: error.message || 'Failed to update tenant.',
                            })
                        );
                    }
                },
            })
        );
    };

    const handleDuplicate = () => {
        if (!id || !selectedTenant) return;

        navigate(`/tenants/${id}/duplicate`);
    };

    const { clusters } = useSelector((state: RootState) => state.clusters);

    const handleReconcile = async () => {
        if (!id) return;
        setKcReconcileLoading(true);
        setKcSteps(null);
        try {
            // kcCheck / steps / message are TOP-LEVEL fields of the reconcile response, not nested
            // under `data` like the other tenant endpoints - reading r.data.kcCheck always yielded
            // undefined, so the status strip never refreshed and the toast was always generic.
            const r = await reconcileTenant(id) as unknown as ReconcileResponse;
            if (r.kcCheck) setKcStatus(r.kcCheck);
            const failed = r.failedSteps ?? (r.steps ?? []).filter(st => !st.ok);
            setKcSteps(r.steps ?? null);
            dispatch(addNotification({
                type: failed.length === 0 ? 'success' : 'warning',
                title: failed.length === 0 ? undefined : 'Reconciled with failures',
                message: r.message ?? 'Keycloak reconciled.',
            }));
            dispatch(fetchTenantById(id));
        } catch (err: any) {
            // A fatal step still returns the partial report - show how far it got.
            const body: ReconcileResponse | undefined = err?.response?.data;
            setKcSteps(body?.steps ?? null);
            dispatch(addNotification({ type: 'error', title: 'Reconcile Failed', message: body?.message ?? err.message }));
        } finally {
            setKcReconcileLoading(false);
        }
    };

    const handleKcRefresh = async () => {
        if (!id) return;
        setKcStatusLoading(true);
        try {
            const r = await getKeycloakStatus(id);
            if (r.data) setKcStatus(r.data);
        } catch {} finally {
            setKcStatusLoading(false);
        }
    };

    const handleKcTest = async () => {
        if (!id) return;
        setKcTestLoading(true);
        setKcTestResult(null);
        try {
            const r = await testKeycloakConnection(id);
            setKcTestResult(r.data ?? null);
        } catch (err: any) {
            setKcTestResult({ ok: false, tokenOk: false, listRealmsOk: false, error: err.message });
        } finally {
            setKcTestLoading(false);
        }
    };

    const handleInitializeAllKeys = async () => {
        if (!id || !selectedTenant) return;
        setInitKeysLoading(true);
        try {
            await initializeAllKeys(selectedTenant.tenantId);
            dispatch(addNotification({ type: 'success', title: 'Keys Initialized', message: 'All service keys initialized successfully.' }));
            // Re-run diagnostics to refresh key status
            const r = await runTenantDiagnostics(id);
            setDiagnosticsResult(r.data ?? null);
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Key Initialization Failed', message: err.message }));
        } finally {
            setInitKeysLoading(false);
        }
    };

    const handleRunDiagnostics = async () => {
        if (!id) return;
        setDiagnosticsLoading(true);
        setDiagnosticsResult(null);
        try {
            const r = await runTenantDiagnostics(id);
            setDiagnosticsResult(r.data ?? null);
        } catch (err: any) {
            setDiagnosticsResult({ status: 'error', message: err.message, tenantId: id, keycloakUrl: '', realmName: '', checks: {}, timestamp: new Date().toISOString() });
        } finally {
            setDiagnosticsLoading(false);
        }
    };

    const handleJwksSync = async () => {
        if (!id) return;
        setJwksSyncLoading(true);
        setJwksSyncResult(null);
        try {
            const r = await syncJwksUrls(id);
            if (r.data) setJwksSyncResult(r.data);
            dispatch(addNotification({
                type: r.success ? 'success' : 'error',
                message: r.success
                    ? `JWKS URL pushed to ${r.data?.updated.length ?? 0} client(s).`
                    : (r.message ?? 'JWKS sync failed'),
            }));
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'JWKS Sync Failed', message: err.message }));
        } finally {
            setJwksSyncLoading(false);
        }
    };

    // Manual retry sync — uses server-side KC admin credentials (no credentials in UI).
    const handleSyncRetry = async () => {
        if (!id || !selectedTenant) return;
        setSyncRetryLoading(true);
        try {
            const providers = selectedTenant.authenticationProviders ?? [];
            const kc = (selectedTenant as any).keycloak ?? {};
            // Derive current OTP/magic-link state from keycloak.clients (the source of truth)
            const authMethods = {
                credential: true,
                otp: !!kc.clients?.externalOtp,
                magicLink: !!kc.clients?.externalMagicLink,
                federatedAd: null,
            };

            const result = await syncTenantAuth(id, { authMethods });
            dispatch(addNotification({ type: 'success', message: result.data?.message ?? 'Keycloak sync complete.' }));
            dispatch(fetchTenantById(id));
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Sync Failed', message: err.message || 'Keycloak sync failed.' }));
        } finally {
            setSyncRetryLoading(false);
        }
    };

    const handleSaveKcTimeouts = async () => {
        if (!id) return;
        setKcTimeoutsSaving(true);
        try {
            await syncSessionTimeouts(id, kcTimeoutsDraft);
            dispatch(addNotification({ type: 'success', message: 'Keycloak session timeouts updated.' }));
            dispatch(fetchTenantById(id));
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Sync Failed', message: err.message }));
        } finally {
            setKcTimeoutsSaving(false);
        }
    };

    const handleSaveSessionPolicy = async () => {
        if (!id || !sessionPolicyDraft) return;
        setSessionPolicySaving(true);
        try {
            await updateSessionPolicy(id, sessionPolicyDraft);
            dispatch(addNotification({ type: 'success', message: 'Session policy saved.' }));
            dispatch(fetchTenantById(id));
            setSessionPolicyEditing(false);
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Save Failed', message: err.message }));
        } finally {
            setSessionPolicySaving(false);
        }
    };

    const handleForceLogout = async () => {
        if (!selectedTenant || !forceLogoutUserId.trim()) return;
        setForceLogoutLoading(true);
        try {
            await forceLogoutUser(selectedTenant.tenantId, forceLogoutUserId.trim());
            dispatch(addNotification({ type: 'success', message: `All sessions revoked for user ${forceLogoutUserId.trim()}.` }));
            setForceLogoutUserId('');
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Force Logout Failed', message: err.message }));
        } finally {
            setForceLogoutLoading(false);
        }
    };

    const handleToggleCredential = async (enable: boolean) => {
        if (!selectedTenant) return;
        setLoginMethodSaving(true);
        try {
            await updateLoginMethods((selectedTenant as any)._id ?? selectedTenant.tenantId, enable);
            dispatch(addNotification({ type: 'success', message: `Credential login ${enable ? 'enabled' : 'disabled'}.` }));
            dispatch(fetchTenantById((selectedTenant as any)._id ?? selectedTenant.tenantId));
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Save Failed', message: err.message }));
        } finally {
            setLoginMethodSaving(false);
        }
    };

    const handleToggleSubtenants = async (enable: boolean) => {
        if (!selectedTenant) return;
        setSubtenantsSaving(true);
        try {
            await updateSubtenantsEnabled((selectedTenant as any)._id ?? selectedTenant.tenantId, enable);
            dispatch(addNotification({ type: 'success', message: `Sub-tenant scoping ${enable ? 'enabled' : 'disabled'}.` }));
            dispatch(fetchTenantById((selectedTenant as any)._id ?? selectedTenant.tenantId));
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Save Failed', message: err.message }));
        } finally {
            setSubtenantsSaving(false);
        }
    };

    const handleSaveSecuritySettings = async () => {
        if (!selectedTenant || !securityDraft) return;
        setSecuritySaving(true);
        try {
            await updateSecuritySettings((selectedTenant as any)._id ?? selectedTenant.tenantId, securityDraft);
            dispatch(addNotification({ type: 'success', message: 'Security settings saved.' }));
            setSecurityEditing(false);
            dispatch(fetchTenantById((selectedTenant as any)._id ?? selectedTenant.tenantId));
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Save Failed', message: err.message }));
        } finally {
            setSecuritySaving(false);
        }
    };

    const handleSaveSignatureProvider = async () => {
        if (!selectedTenant || !signatureProviderDraft) return;
        setSignatureProviderSaving(true);
        try {
            // Send the provider plus ONLY that provider's config block. When provider is
            // None, just the discriminator.
            //
            // This was a two-branch ternary that fell through to `{ provider: 'None' }` for
            // anything that was not literally 'Skribble' — so selecting DocuSign in the
            // dropdown would have persisted None and still reported success. Any provider
            // added to SIGNATURE_PROVIDERS needs a branch here as well as a merge branch in
            // tenantController.updateSignatureProviderSettings.
            const payload: SignatureProviderSettings =
                signatureProviderDraft.provider === 'Skribble'
                    ? { provider: 'Skribble', skribble: { ...defaultSkribbleSettings, ...signatureProviderDraft.skribble } }
                    : signatureProviderDraft.provider === 'DocuSign'
                        ? { provider: 'DocuSign', docusign: { ...defaultDocuSignSettings, ...signatureProviderDraft.docusign } }
                        : { provider: 'None' };
            await updateSignatureProviderSettings((selectedTenant as any)._id ?? selectedTenant.tenantId, payload);
            dispatch(addNotification({ type: 'success', message: 'Signature provider settings saved.' }));
            setSignatureProviderEditing(false);
            dispatch(fetchTenantById((selectedTenant as any)._id ?? selectedTenant.tenantId));
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Save Failed', message: err.message }));
        } finally {
            setSignatureProviderSaving(false);
        }
    };

    const handleSaveDedicatedTypes = async () => {
        if (!id) return;
        setDedicatedTypesSaving(true);
        try {
            const result = await updateDedicatedDeploymentObjectTypes(id, dedicatedTypesDraft);
            if (result.data) {
                dispatch(fetchTenantById(id));
                dispatch(addNotification({ type: 'success', message: 'Dedicated deployment types updated.' }));
            }
            setDedicatedTypesEditing(false);
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Update Failed', message: err.message }));
        } finally {
            setDedicatedTypesSaving(false);
        }
    };

    // ── Encryption key handlers ───────────────────────────────────────────────

    const handleRefreshEncKeyStatus = async () => {
        if (!selectedTenant?.tenantId) return;
        setEncKeyStatusLoading(true);
        setEncKeyStatusError(null);
        try {
            const r = await encKeyApi.getEncryptionKeyStatus(selectedTenant.tenantId);
            if (r?.data) setEncKeyStatus(r.data);
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to load encryption key status.';
            setEncKeyStatusError(msg);
        } finally {
            setEncKeyStatusLoading(false);
        }
    };

    const handleInitializeEncryptionKey = async () => {
        if (!selectedTenant?.tenantId) return;
        setEncKeyInitLoading(true);
        try {
            await encKeyApi.initializeEncryptionKey(selectedTenant.tenantId);
            dispatch(addNotification({ type: 'success', title: 'Encryption Key Initialized', message: 'Encryption root key provisioned successfully.' }));
            await handleRefreshEncKeyStatus();
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Initialization Failed', message: err.message ?? 'Could not initialize encryption key.' }));
        } finally {
            setEncKeyInitLoading(false);
        }
    };

    const handleRotateEncryptionKey = async () => {
        if (!selectedTenant?.tenantId) return;
        setShowRotateConfirm(false);
        setEncKeyRotateLoading(true);
        try {
            await encKeyApi.rotateEncryptionKey(selectedTenant.tenantId);
            dispatch(addNotification({ type: 'success', title: 'Encryption Key Rotated', message: 'Encryption key rotated. All active sessions have been invalidated.' }));
            await handleRefreshEncKeyStatus();
        } catch (err: any) {
            dispatch(addNotification({ type: 'error', title: 'Rotation Failed', message: err.message ?? 'Could not rotate encryption key.' }));
        } finally {
            setEncKeyRotateLoading(false);
        }
    };

    // Prepare initial form values when entering edit mode
    const getInitialFormValues = () => {
        if (!selectedTenant) return null;

        const providers = selectedTenant.authenticationProviders ?? [];

        // Normalize url: prefer subdomain; fall back to baseUrl with protocol stripped.
        // Tenants created via /provision store baseUrl, not subdomain.
        const urlValue = selectedTenant.subdomain
            || (selectedTenant.baseUrl?.replace(/^https?:\/\//, '') ?? '');

        const t = selectedTenant as any;
        // OIDC providers = authenticationProviders where type === 'oidc'
        const oidcProviders = providers.filter((p: any) => p.type === 'oidc');
        // OTP / magic link = controlled by keycloak.clients, NOT authenticationProviders
        const kc = t.keycloak ?? {};

        return {
            ...selectedTenant,
            url: urlValue,
            useSsl: selectedTenant.useSsl !== false,
            port: t.port ?? null,
            createK8sIngress: t.createK8sIngress || false,
            provisioningInternalMode: t.provisioning?.internal?.mode ?? 'AutoProvision',
            provisioningExternalMode: t.provisioning?.external?.mode ?? 'AutoProvision',
            redisUsername:    t.redis?.username ?? '',
            redisPasswordKey: t.redis?.passwordSecretKey ?? '',
            // Keycloak infrastructure fields
            kcBaseUrl:       kc.baseUrl ?? '',
            kcJwksBaseUrl:   kc.jwksBaseUrl ?? 'http://identity-provider:8080',
            kcAppClientId:   kc.appClientId ?? '',
            kcClientSecret:  kc.clientSecret ?? '',
            kcRedirectUri:   kc.redirectUri ?? '',
            // OIDC providers — shown and editable in the form
            oidcProviders,
            // External login methods — derived from keycloak.clients
            authOtp:       !!kc.clients?.externalOtp,
            authMagicLink: !!kc.clients?.externalMagicLink,
        };
    };

    if (loadingTenant && !selectedTenant) {
        return (
            <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (!selectedTenant && !loadingTenant) {
        return (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                <div className="text-center">
                    <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">Tenant Not Found</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        The tenant you're looking for does not exist or you do not have permission to view it.
                    </p>
                    <div className="mt-6">
                        <button
                            onClick={() => navigate('/tenants')}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <FiArrowLeft className="mr-2" />
                            Back to Tenants
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!selectedTenant) return null;

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
            case 'disabled':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
            case 'maintenance':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
            case 'deleted':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
            default:
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header with back button and actions */}
            <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
                <div className="flex items-center">
                    <button
                        onClick={() => navigate('/tenants')}
                        className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                        <FiArrowLeft className="mr-2" />
                        Back to Tenants
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedTenant.tenantId}
                        </h1>
                        <div className="mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${getStatusBadgeColor(selectedTenant.status)}`}>
                                {selectedTenant.status}
                            </span>
                            {selectedTenant.dnsCreated && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                    DNS Active
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    {/* KC sync status is shown in the panel below */}

                    {/* Status Management Button */}
                    {selectedTenant.status !== 'deleted' && (
                        <button
                            onClick={() => setShowStatusManager(!showStatusManager)}
                            className={`inline-flex items-center px-3 py-2 border ${showStatusManager ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800' : 'border-transparent text-white bg-blue-600 hover:bg-blue-700'} shadow-sm text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                        >
                            {showStatusManager ? (
                                <>
                                    <FiX className="mr-2" />
                                    Hide Status Manager
                                </>
                            ) : (
                                <>
                                    <FiRefreshCw className="mr-2" />
                                    Manage Status
                                </>
                            )}
                        </button>
                    )}

                    {!isEditing ? (
                        <>
                            <button
                                onClick={handleDuplicate}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                <FiCopy className="mr-2" />
                                Duplicate
                            </button>
                            <button
                                onClick={() => setIsEditing(true)}
                                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                <FiEdit2 className="mr-2" />
                                Edit Tenant
                            </button>
                            <button
                                onClick={handleDelete}
                                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                                <FiTrash2 className="mr-2" />
                                Delete
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={() => setIsEditing(false)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            <FiX className="mr-2" />
                            Cancel Editing
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
                    {error}
                </div>
            )}

            {/* Keycloak Status Panel */}
            {selectedTenant.status !== 'deleted' && (() => {
                const overall = kcStatus?.overall as KcOverallStatus | undefined;

                const overallCfg: Record<KcOverallStatus, { bar: string; label: string; text: string }> = {
                    synced:       { bar: 'bg-green-500',  label: 'Synced',         text: 'Keycloak matches MongoDB.' },
                    partial:      { bar: 'bg-yellow-500', label: 'Partial',         text: 'Some clients or flows are missing or disabled.' },
                    missing:      { bar: 'bg-red-500',    label: 'Realm missing',   text: 'The Keycloak realm does not exist. Provision this tenant.' },
                    unreachable:  { bar: 'bg-gray-400',   label: 'Unreachable',     text: kcStatus?.error ?? 'Could not reach Keycloak admin API.' },
                    unconfigured: { bar: 'bg-gray-300',   label: 'Not configured',  text: 'No Keycloak config in this tenant document.' },
                };

                const componentLabel: Record<string, string> = {
                    realm: 'Realm', appClient: 'App client', otpClient: 'OTP client',
                    magicLinkClient: 'Magic link client', otpFlow: 'OTP flow', magicLinkFlow: 'Magic link flow',
                    wordAddinClient: 'Word Add-in client',
                };
                const componentColor = (s: string) => {
                    if (s === 'ok')             return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
                    if (s === 'disabled')        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
                    if (s === 'missing')         return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
                    if (s === 'not_configured')  return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
                    return 'bg-gray-100 text-gray-600';
                };

                const cfg = overall ? overallCfg[overall] : null;

                return (
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <div className={`h-1 ${cfg?.bar ?? 'bg-gray-200'}`} />
                        <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-3">
                                {kcStatusLoading
                                    ? <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                    : <span className={`inline-block h-3 w-3 rounded-full ${cfg?.bar ?? 'bg-gray-200'}`} />}
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Keycloak: <strong>{cfg?.label ?? 'Checking...'}</strong>
                                </span>
                                {cfg && <span className="text-xs text-gray-500 dark:text-gray-400">{cfg.text}</span>}
                                {kcStatus?.checkedAt && (
                                    <span className="text-xs text-gray-400">
                                        · checked {Math.round((Date.now() - new Date(kcStatus.checkedAt).getTime()) / 60000)} min ago
                                    </span>
                                )}
                            </div>

                            {/* Component badges */}
                            {kcStatus && overall !== 'unreachable' && overall !== 'unconfigured' && (
                                <div className="flex flex-wrap gap-1.5">
                                    {(Object.entries(kcStatus) as [string, any][])
                                        .filter(([k]) => Object.keys(componentLabel).includes(k))
                                        .map(([k, v]) => v !== 'not_configured' && (
                                            <span key={k} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${componentColor(v)}`}>
                                                {componentLabel[k]}: {v}
                                            </span>
                                        ))}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-2 ml-auto">
                                {(selectedTenant as any).keycloak?.baseUrl && (() => {
                                    const base: string = (selectedTenant as any).keycloak.baseUrl;
                                    const realm: string = (selectedTenant as any).keycloak?.realmName ?? selectedTenant.tenantId;
                                    const adminBase = base.replace('://auth.', '://auth-admin.');
                                    const adminUrl = `${adminBase}/admin/${realm}/console`;
                                    return (
                                        <a href={adminUrl} target="_blank" rel="noopener noreferrer"
                                            title="Open Keycloak Admin Console"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <FiExternalLink className="h-3 w-3" />
                                            Admin
                                        </a>
                                    );
                                })()}
                                <button onClick={handleKcRefresh} disabled={kcStatusLoading}
                                    title="Refresh KC status"
                                    className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">
                                    <FiRefreshCw className={`h-4 w-4 ${kcStatusLoading ? 'animate-spin' : ''}`} />
                                </button>
                                <button onClick={handleKcTest} disabled={kcTestLoading}
                                    title="Test Keycloak admin credentials"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                                    {kcTestLoading ? <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg> : null}
                                    Test Credentials
                                </button>
                                {overall && overall !== 'unconfigured' && (
                                    <button onClick={handleReconcile} disabled={kcReconcileLoading}
                                        title="Push MongoDB config to Keycloak: clients, flows, service roles, JWKS URLs"
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border disabled:opacity-50 transition-colors ${
                                            overall === 'synced'
                                                ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                : overall === 'partial'
                                                    ? 'border-amber-400 dark:border-amber-600 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                                                    : overall === 'missing'
                                                        ? 'border-red-400 dark:border-red-600 text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600'
                                                        : 'border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100'
                                        }`}>
                                        {kcReconcileLoading
                                            ? <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                            : <FiZap className="h-3 w-3" />}
                                        Reconcile
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Reconcile step report - only rendered when something did not complete.
                            A clean reconcile stays silent so the strip does not grow noise. */}
                        {kcSteps && kcSteps.some(st => !st.ok) && (
                            <div className="mx-4 mb-3 px-3 py-2 rounded text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="font-medium">
                                        Reconcile finished with {kcSteps.filter(st => !st.ok).length} of {kcSteps.length} step(s) incomplete
                                    </span>
                                    <button onClick={() => setKcSteps(null)}
                                        className="text-amber-700 dark:text-amber-400 hover:underline">dismiss</button>
                                </div>
                                <ul className="space-y-0.5">
                                    {kcSteps.filter(st => !st.ok).map(st => (
                                        <li key={st.step} className="font-mono">
                                            <span className="font-semibold">{st.step}</span>
                                            {st.fatal ? ' [fatal]' : st.skipped ? ' [skipped]' : ''}
                                            {st.error ? ` - ${st.error}` : ''}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-1 text-amber-700 dark:text-amber-400">
                                    The tenant document was still updated. Steps not listed here completed normally.
                                </div>
                            </div>
                        )}

                        {/* Test connection result */}
                        {kcTestResult && (
                            <div className={`mx-4 mb-3 px-3 py-2 rounded text-xs ${kcTestResult.ok ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'}`}>
                                {kcTestResult.ok
                                    ? `✓ Credentials OK — token obtained, ${kcTestResult.realmCount} realm(s) listed.`
                                    : <>
                                        <strong>✗ {kcTestResult.error}</strong>
                                        {kcTestResult.hint && <div className="mt-1 text-red-600 dark:text-red-400">{kcTestResult.hint}</div>}
                                      </>
                                }
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Tenant readiness (Phase 2, probes only) — supersedes the Diagnostics block below. */}
            {selectedTenant.status !== 'deleted' && id && (
                <TenantReadinessPanel tenantId={id} tenant={selectedTenant} />
            )}

            {/* Diagnostics */}
            {selectedTenant.status !== 'deleted' && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg px-4 py-3">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <FiRefreshCw className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Diagnostics</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">— test Keycloak connectivity for this tenant</span>
                        </div>
                        <button
                            onClick={handleRunDiagnostics}
                            disabled={diagnosticsLoading}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                            {diagnosticsLoading
                                ? <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                : <FiRefreshCw className="h-3 w-3" />}
                            Run diagnostics
                        </button>
                    </div>
                    {diagnosticsResult && (
                        <div className="mt-3 space-y-1">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                Keycloak: <span className="font-mono">{diagnosticsResult.keycloakUrl}</span> — realm: <span className="font-mono">{diagnosticsResult.realmName}</span>
                            </div>
                            {Object.entries(diagnosticsResult.checks).map(([key, check]) => (
                                <div key={key} className={`flex items-center justify-between px-3 py-1.5 rounded text-xs ${
                                    check.status === 'ok'    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' :
                                    check.status === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' :
                                                               'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                                }`}>
                                    <span className="font-medium">{key}</span>
                                    <span className="text-right ml-4">{check.message}{check.latencyMs ? ` (${check.latencyMs}ms)` : ''}{check.detail ? ` — ${check.detail}` : ''}</span>
                                </div>
                            ))}
                            {(diagnosticsResult.missingKeys?.length ?? 0) > 0 && (
                                <div className="flex items-center justify-between px-3 py-2 rounded bg-red-50 dark:bg-red-900/20 mt-2">
                                    <span className="text-xs text-red-700 dark:text-red-400 font-medium">
                                        {diagnosticsResult.missingKeys!.length} service(s) missing keys — login will fail
                                    </span>
                                    <button
                                        onClick={handleInitializeAllKeys}
                                        disabled={initKeysLoading}
                                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                                        {initKeysLoading
                                            ? <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                            : <FiZap className="h-3 w-3" />}
                                        Initialize all keys
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Key Management quick actions */}
            {selectedTenant.status !== 'deleted' && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                        <FiKey className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Service Keys</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">— manage JWKS signing keys per service</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate(`/key-management?tenantId=${selectedTenant.tenantId}`)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <FiKey className="h-3 w-3" /> View key status
                        </button>
                    </div>
                </div>
            )}

            {/* Encryption Key */}
            {selectedTenant.status !== 'deleted' && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <div className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <FiRotateCw className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Encryption Key</span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">— per-tenant authenticated encryption for session data</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleRefreshEncKeyStatus}
                                disabled={encKeyStatusLoading}
                                title="Refresh encryption key status"
                                className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40">
                                <FiRefreshCw className={`h-4 w-4 ${encKeyStatusLoading ? 'animate-spin' : ''}`} />
                            </button>
                            {encKeyStatus?.initialized === false && (
                                <button
                                    onClick={handleInitializeEncryptionKey}
                                    disabled={encKeyInitLoading}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-indigo-300 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 disabled:opacity-50">
                                    {encKeyInitLoading
                                        ? <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                        : <FiKey className="h-3 w-3" />}
                                    Initialize Encryption Key
                                </button>
                            )}
                            {encKeyStatus?.initialized && (
                                <button
                                    onClick={() => setShowRotateConfirm(true)}
                                    disabled={encKeyRotateLoading}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border disabled:opacity-50 transition-colors ${
                                        encKeyStatus.rotationNeeded
                                            ? 'border-amber-400 dark:border-amber-600 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100'
                                            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}>
                                    {encKeyRotateLoading
                                        ? <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                        : <FiRotateCw className="h-3 w-3" />}
                                    Rotate Encryption Key
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Status summary — metadata only, never key bytes */}
                    {encKeyStatus && (
                        <div className="px-4 pb-3">
                            {encKeyStatus.initialized ? (
                                <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                                    {encKeyStatus.keyVersion !== undefined && (
                                        <span>Version: <span className="font-mono font-medium text-gray-700 dark:text-gray-200">{encKeyStatus.keyVersion}</span></span>
                                    )}
                                    {encKeyStatus.createdAt && (
                                        <span>Created: <span className="font-medium text-gray-700 dark:text-gray-200">{new Date(encKeyStatus.createdAt).toLocaleDateString()}</span></span>
                                    )}
                                    {encKeyStatus.expiresAt && (
                                        <span>Expires: <span className="font-medium text-gray-700 dark:text-gray-200">{new Date(encKeyStatus.expiresAt).toLocaleDateString()}</span></span>
                                    )}
                                    {encKeyStatus.rotationNeeded && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 font-medium">
                                            Rotation recommended
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                    No encryption key provisioned for this tenant. Click "Initialize Encryption Key" to provision one.
                                </p>
                            )}
                        </div>
                    )}
                    {encKeyStatusLoading && !encKeyStatus && (
                        <div className="px-4 pb-3">
                            <span className="text-xs text-gray-400 dark:text-gray-500">Loading encryption key status…</span>
                        </div>
                    )}
                    {!encKeyStatusLoading && encKeyStatusError && (
                        <div className="px-4 pb-3">
                            <div className="flex items-start gap-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-400">
                                <span className="shrink-0 font-medium">Failed to load encryption key status:</span>
                                <span className="font-mono">{encKeyStatusError}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Encryption Key rotation confirmation dialog */}
            {showRotateConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowRotateConfirm(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                                <FiRotateCw className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Rotate Encryption Key</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                            Rotating this key will sign out all active sessions for this tenant. Users will be required to re-login. Continue?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowRotateConfirm(false)}
                                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                                Cancel
                            </button>
                            <button
                                onClick={handleRotateEncryptionKey}
                                className="px-4 py-2 text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500">
                                Rotate &amp; Sign Out All Sessions
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Status Manager */}
            {showStatusManager && selectedTenant.status !== 'deleted' && (
                <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <TenantStatusManager
                            tenantId={id || ''}
                            currentStatus={selectedTenant.status as any}
                            onStatusChanged={() => {
                                if (id) {
                                    dispatch(fetchTenantById(id));
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
                {isEditing ? (
                    <Formik
                        initialValues={getInitialFormValues() || {}}
                        validationSchema={TenantSchema}
                        onSubmit={handleUpdateTenant}
                        enableReinitialize
                    >
                        {(formikProps) => {
                            // Type safe way to access values
                            const { values, isValid, dirty } = formikProps;
                            // Extend Tenant with the extra form-only fields that aren't in the DB type
                            const typedValues = values as Tenant & {
                                url: string;
                                useSsl: boolean;
                                port: number | null;
                                createK8sIngress: boolean;
                                oidcProviders: any[];
                                authOtp: boolean;
                                authMagicLink: boolean;
                                provisioningInternalMode: string;
                                provisioningExternalMode: string;
                                redisUsername: string;
                                redisPasswordKey: string;
                            };

                            return (
                                <Form className="p-6 space-y-6">
                                    {/* Basic Tenant Information Section */}
                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                            Basic Information
                                        </h3>

                                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                            <div className="sm:col-span-6">
                                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Name
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="name"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                                <ErrorMessage name="name" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>

                                            <div className="sm:col-span-3">
                                                <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Tenant ID
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="tenantId"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                                <ErrorMessage name="tenantId" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* URL, DNS, and Ingress Configuration Section */}
                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                            URL & Access Configuration
                                        </h3>

                                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                            <div className="sm:col-span-6">
                                                <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    URL
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="url"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                        placeholder="e.g. tenant-name.example.com"
                                                    />
                                                </div>
                                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                    This URL will be used for both DNS entry and Kubernetes Ingress if enabled.
                                                </p>
                                                <ErrorMessage name="url" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>

                                            <div className="sm:col-span-2 flex items-center gap-3">
                                                <Field type="checkbox" name="useSsl"
                                                    className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded" />
                                                <label htmlFor="useSsl" className="text-sm font-medium text-gray-700 dark:text-gray-300">Use SSL (https)</label>
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Port <span className="text-gray-400 text-xs">(blank = default)</span>
                                                </label>
                                                <Field name="port" type="number"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                            </div>
                                            <div className="sm:col-span-2" />

                                            <div className="sm:col-span-3">
                                                <div className="relative flex items-start">
                                                    <div className="flex items-center h-5">
                                                        <Field
                                                            type="checkbox"
                                                            name="dnsCreated"
                                                            className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded"
                                                        />
                                                    </div>
                                                    <div className="ml-3 text-sm">
                                                        <label htmlFor="dnsCreated" className="font-medium text-gray-700 dark:text-gray-300">
                                                            DNS Entry Created
                                                        </label>
                                                        <p className="text-gray-500 dark:text-gray-400">
                                                            Indicates whether a DNS entry exists for this tenant's URL.
                                                            Note: Changing this manually won't create or delete actual DNS records.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="sm:col-span-3">
                                                <div className="relative flex items-start">
                                                    <div className="flex items-center h-5">
                                                        <Field
                                                            type="checkbox"
                                                            name="createK8sIngress"
                                                            className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded"
                                                        />
                                                    </div>
                                                    <div className="ml-3 text-sm">
                                                        <label htmlFor="createK8sIngress" className="font-medium text-gray-700 dark:text-gray-300">
                                                            Create Kubernetes Ingress
                                                        </label>
                                                        <p className="text-gray-500 dark:text-gray-400">
                                                            Creates an Nginx ingress with Let's Encrypt SSL certificate
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {typedValues.createK8sIngress && (
                                                <div className="sm:col-span-6">
                                                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                                                            Kubernetes Ingress Configuration
                                                        </h4>

                                                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                                            <div className="sm:col-span-3">
                                                                <label htmlFor="nodeSelector.key" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                    Node Selector Key
                                                                </label>
                                                                <div className="mt-1">
                                                                    <Field
                                                                        name="nodeSelector.key"
                                                                        type="text"
                                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                        placeholder="e.g., tenant"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="sm:col-span-3">
                                                                <label htmlFor="nodeSelector.value" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                    Node Selector Value
                                                                </label>
                                                                <div className="mt-1">
                                                                    <Field
                                                                        name="nodeSelector.value"
                                                                        type="text"
                                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                        placeholder="e.g., tenant1"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="sm:col-span-3">
                                                                <label htmlFor="serviceName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                    Service Name
                                                                </label>
                                                                <div className="mt-1">
                                                                    <Field
                                                                        name="serviceName"
                                                                        type="text"
                                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                        placeholder="e.g., tenant-service"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="sm:col-span-3">
                                                                <label htmlFor="servicePort" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                    Service Port
                                                                </label>
                                                                <div className="mt-1">
                                                                    <Field
                                                                        name="servicePort"
                                                                        type="number"
                                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                        placeholder="e.g., 80"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Database and Cluster Configuration Section */}
                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                            Database & Cluster Configuration
                                        </h3>

                                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                            <div className="sm:col-span-3">
                                                <label htmlFor="databaseName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Database Name
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="databaseName"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                                <ErrorMessage name="databaseName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>

                                            <div className="sm:col-span-3">
                                                <label htmlFor="cluster" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Cluster
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        as="select"
                                                        name="cluster"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    >
                                                        <option value="">Select a cluster</option>
                                                        {clusters.map((cluster: Cluster) => (
                                                            <option key={cluster._id} value={cluster.clusterId}>
                                                                {cluster.clusterId}
                                                            </option>
                                                        ))}
                                                    </Field>
                                                </div>
                                                <ErrorMessage name="cluster" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                            </div>

                                            <div className="sm:col-span-6">
                                                <label htmlFor="secretStoreName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Secret Store Name
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="secretStoreName"
                                                        type="text"
                                                        placeholder="e.g. my-tenant-vault"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono"
                                                    />
                                                </div>
                                                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                                                    Azure Key Vault name for this tenant. Services look up secrets at <code>https://&#123;secretStoreName&#125;.vault.azure.net/</code>.
                                                    Used by the identity provider to resolve service authentication keys.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* OIDC Providers */}
                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">OIDC Providers</h3>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Identity providers stored in <code className="text-xs">authenticationProviders</code>. The credential entry is always preserved.</p>
                                        <FieldArray name="oidcProviders">
                                            {({ push, remove }) => (
                                                <div className="mt-4 space-y-4">
                                                    {typedValues.oidcProviders?.map((_p: any, i: number) => (
                                                        <div key={i} className="bg-gray-50 dark:bg-gray-700 rounded-md p-4 relative">
                                                            <button type="button" onClick={() => remove(i)}
                                                                className="absolute top-2 right-2 text-red-500 hover:text-red-700">
                                                                <FiTrash2 className="h-4 w-4" />
                                                            </button>
                                                            <div className="grid grid-cols-1 gap-y-3 gap-x-4 sm:grid-cols-6">
                                                                {[
                                                                    ['Label',        `oidcProviders.${i}.label`,       ''],
                                                                    ['Alias',        `oidcProviders.${i}.alias`,       ''],
                                                                    ['Client ID',    `oidcProviders.${i}.clientId`,   ''],
                                                                    ['Client Secret', `oidcProviders.${i}.clientSecret`, ''],
                                                                    ['Azure Tenant ID', `oidcProviders.${i}.tenantId`, ''],
                                                                    ['Provider Name', `oidcProviders.${i}.providerName`, 'AzureAD'],
                                                                ].map(([label, name, placeholder]) => (
                                                                    <div key={name} className="sm:col-span-3">
                                                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{label}</label>
                                                                        <Field name={name} type={name.includes('Secret') ? 'password' : 'text'} placeholder={placeholder}
                                                                            className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button type="button"
                                                        onClick={() => push({ type: 'oidc', providerId: '', label: '', alias: '', clientId: '', clientSecret: '', tenantId: '', providerName: 'AzureAD', enabled: true })}
                                                        className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                                                        <FiPlus className="mr-2 h-4 w-4" /> Add OIDC Provider
                                                    </button>
                                                </div>
                                            )}
                                        </FieldArray>
                                    </div>

                                    {/* Keycloak JWKS Configuration */}
                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Keycloak Service Configuration</h3>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            Infrastructure settings for Keycloak provisioning and service client JWKS endpoints.
                                        </p>
                                        <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6 mt-4">
                                            <div className="sm:col-span-4">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Keycloak Base URL
                                                </label>
                                                <Field name="kcBaseUrl" type="text"
                                                    placeholder="https://keycloak.example.com"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono" />
                                                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Stored as <code>keycloak.baseUrl</code>. Used for admin API calls.</p>
                                            </div>
                                            <div className="sm:col-span-4">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    JWKS Base URL
                                                </label>
                                                <Field name="kcJwksBaseUrl" type="text"
                                                    placeholder="http://identity-provider:8080  (Docker/K8s internal)"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono" />
                                                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                                                    Stored as <code>keycloak.jwksBaseUrl</code>. Keycloak uses this to fetch JWKS from each service for token validation.
                                                    Full JWKS URL = <code>{'{jwksBaseUrl}'}/realms/{'{tenantId}'}/services/{'{service}'}/.well-known/jwks.json</code>
                                                </p>
                                            </div>
                                            <div className="sm:col-span-4">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    BFF App Client ID
                                                </label>
                                                <Field name="kcAppClientId" type="text"
                                                    placeholder="ontoris-client"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono" />
                                                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Stored as <code>keycloak.appClientId</code>. The Keycloak client the BFF uses for the PKCE login flow.</p>
                                            </div>
                                            <div className="sm:col-span-4">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    BFF App Client Secret
                                                </label>
                                                <Field name="kcClientSecret" type="password"
                                                    placeholder="••••••••"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono" />
                                                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Stored as <code>keycloak.clientSecret</code>. Leave blank to keep the existing value.</p>
                                            </div>
                                            <div className="sm:col-span-6">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    BFF Redirect URI
                                                </label>
                                                <Field name="kcRedirectUri" type="text"
                                                    placeholder="https://dev.ontoris.app/api/auth/keycloak/callback"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono" />
                                                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Stored as <code>keycloak.redirectUri</code>. Must match the redirect URI registered in Keycloak for the app client.</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* External Login Methods (keycloak.clients) */}
                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">External Login Methods</h3>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            Passwordless methods for external users. Controls <code className="text-xs">keycloak.clients.externalOtp</code> / <code className="text-xs">externalMagicLink</code>. Save syncs Keycloak automatically.
                                        </p>
                                        <div className="mt-4 space-y-3">
                                            <div className="flex items-start">
                                                <Field type="checkbox" name="authOtp" className="mt-0.5 h-4 w-4 border-gray-300 rounded" />
                                                <div className="ml-3">
                                                    <label htmlFor="authOtp" className="text-sm font-medium text-gray-700 dark:text-gray-300">Email OTP</label>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">6-digit code via email (<code className="text-xs">email-otp-browser</code> flow).</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start">
                                                <Field type="checkbox" name="authMagicLink" className="mt-0.5 h-4 w-4 border-gray-300 rounded" />
                                                <div className="ml-3">
                                                    <label htmlFor="authMagicLink" className="text-sm font-medium text-gray-700 dark:text-gray-300">Magic Link</label>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">Single-use login link via email (<code className="text-xs">magic-link-browser</code> flow).</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Provisioning Section */}
                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Provisioning</h3>
                                        <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6 mt-4">
                                            <div className="sm:col-span-3">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Internal Users</label>
                                                <Field as="select" name="provisioningInternalMode"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md">
                                                    <option value="AutoProvision">AutoProvision</option>
                                                    <option value="InviteOnly">InviteOnly</option>
                                                    <option value="Disabled">Disabled</option>
                                                </Field>
                                            </div>
                                            <div className="sm:col-span-3">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">External Users</label>
                                                <Field as="select" name="provisioningExternalMode"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md">
                                                    <option value="AutoProvision">AutoProvision</option>
                                                    <option value="InviteOnly">InviteOnly</option>
                                                    <option value="Disabled">Disabled</option>
                                                </Field>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Redis Section */}
                                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Redis Session Store</h3>
                                        <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6 mt-4">
                                            <div className="sm:col-span-3">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
                                                <Field name="redisUsername" type="text"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono" />
                                            </div>
                                            <div className="sm:col-span-3">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password Key (Vault)</label>
                                                <Field name="redisPasswordKey" type="text"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md font-mono" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Email Configuration Section */}
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                            Email Configuration
                                        </h3>

                                        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                            <div className="sm:col-span-3">
                                                <label htmlFor="email.sendEmailNotificationAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Notification Email Address
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="email.sendEmailNotificationAddress"
                                                        type="email"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                            </div>

                                            <div className="sm:col-span-3">
                                                <label htmlFor="email.sendEmailNotifcationDisplayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Notification Display Name
                                                </label>
                                                <div className="mt-1">
                                                    <Field
                                                        name="email.sendEmailNotifcationDisplayName"
                                                        type="text"
                                                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-3 pt-5 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            type="button"
                                            onClick={() => setIsEditing(false)}
                                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                        >
                                            <FiX className="mr-2" />
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={!(isValid && dirty) || loadingTenant}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed"
                                        >
                                            {loadingTenant ? (
                                                <>
                                                    <svg
                                                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        ></circle>
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        ></path>
                                                    </svg>
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    <FiSave className="mr-2" />
                                                    Save Changes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                ) : (
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Tenant Details</h3>
                                <dl className="mt-4 space-y-4">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Tenant ID</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTenant.tenantId}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTenant.name || '-'}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">URL</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            {selectedTenant.subdomain}
                                            {selectedTenant.dnsCreated && (
                                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                                    DNS Active
                                                </span>
                                            )}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Database Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTenant.databaseName}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Cluster</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedTenant.cluster}</dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Secret Store Name</dt>
                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">
                                            {selectedTenant.secretStoreName
                                                ? <><span>{selectedTenant.secretStoreName}</span><span className="ml-2 text-xs text-gray-400 font-sans">→ https://{selectedTenant.secretStoreName}.vault.azure.net/</span></>
                                                : <span className="text-red-500 text-xs font-sans">Not set — identity provider cannot resolve service keys</span>
                                            }
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${getStatusBadgeColor(selectedTenant.status)}`}>
                                                {selectedTenant.status}
                                            </span>
                                            {selectedTenant.statusReason && (
                                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                    Reason: {selectedTenant.statusReason}
                                                </p>
                                            )}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created At</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            {new Date(selectedTenant.createdAt).toLocaleString()}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Updated At</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            {new Date(selectedTenant.updatedAt).toLocaleString()}
                                        </dd>
                                    </div>
                                </dl>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Email Configuration</h3>
                                <dl className="mt-4 space-y-4">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Notification Email</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            {selectedTenant.email?.sendEmailNotificationAddress || '-'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Display Name</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            {selectedTenant.email?.sendEmailNotifcationDisplayName || '-'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ACS Endpoint</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            {selectedTenant.email?.acsEndpoint || '-'}
                                        </dd>
                                    </div>
                                </dl>

                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-6">Key Management</h3>
                                <dl className="mt-4 space-y-4">
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Signature Key Manager</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            {selectedTenant.keys?.SignatureKeyManager || '-'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Encryption Key Manager</dt>
                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                            {selectedTenant.keys?.EncryptionKeyManager || '-'}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        {/* Provisioning */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Provisioning</h3>
                            <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Internal Users</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                        {(selectedTenant as any).provisioning?.internal?.mode ?? 'AutoProvision'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">External Users</dt>
                                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                                        {(selectedTenant as any).provisioning?.external?.mode ?? 'AutoProvision'}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {/* Redis */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Redis Session Store</h3>
                            <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                <div>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Username</dt>
                                    <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">
                                        {(selectedTenant as any).redis?.username ?? '-'}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Password Key (Vault)</dt>
                                    <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">
                                        {(selectedTenant as any).redis?.passwordSecretKey ?? '-'}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {/* Security / JWT */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Security / JWT</h3>
                                {!securityEditing ? (
                                    <button
                                        onClick={() => {
                                            const s = (selectedTenant as any).security ?? {};
                                            setSecurityDraft({ ...defaultSecuritySettings, ...s });
                                            setSecurityEditing(true);
                                        }}
                                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        <FiEdit2 className="mr-1.5 h-3.5 w-3.5" /> Edit
                                    </button>
                                ) : (
                                    <div className="flex gap-2">
                                        <button onClick={() => setSecurityEditing(false)}
                                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                                            <FiX className="mr-1.5 h-3.5 w-3.5" /> Cancel
                                        </button>
                                        <button onClick={handleSaveSecuritySettings} disabled={securitySaving}
                                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
                                            <FiSave className="mr-1.5 h-3.5 w-3.5" /> {securitySaving ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!securityEditing ? (
                                <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-4">
                                    {[
                                        ['Algorithm',    (selectedTenant as any).security?.algorithm             ?? defaultSecuritySettings.algorithm],
                                        ['EC Curve',     (selectedTenant as any).security?.ecCurve               ?? defaultSecuritySettings.ecCurve],
                                        ['Key Rotation', `${(selectedTenant as any).security?.keyRotationDays    ?? defaultSecuritySettings.keyRotationDays} days`],
                                        ['Grace Period', `${(selectedTenant as any).security?.keyGracePeriodDays ?? defaultSecuritySettings.keyGracePeriodDays} days`],
                                    ].map(([label, value]) => (
                                        <div key={label}>
                                            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
                                            <dd className="mt-1 text-sm text-gray-900 dark:text-white">{value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            ) : securityDraft && (
                                <div className="space-y-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                        Changing the algorithm or rotation period applies to the next key generation only. Existing active keys are not affected.
                                    </p>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Algorithm</label>
                                            <select value={securityDraft.algorithm}
                                                onChange={e => setSecurityDraft(d => d && ({ ...d, algorithm: e.target.value as any }))}
                                                className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm">
                                                {VALID_ALGORITHMS.map(a => <option key={a} value={a}>{a}</option>)}
                                            </select>
                                        </div>
                                        {securityDraft.algorithm.startsWith('ES') && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">EC Curve</label>
                                                <select value={securityDraft.ecCurve ?? 'P-256'}
                                                    onChange={e => setSecurityDraft(d => d && ({ ...d, ecCurve: e.target.value }))}
                                                    className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm">
                                                    <option value="P-256">P-256</option>
                                                    <option value="P-384">P-384</option>
                                                    <option value="P-521">P-521</option>
                                                </select>
                                            </div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Key rotation (days) <span className="text-gray-400 text-xs">— how long before a key is due for rotation</span>
                                            </label>
                                            <input type="number" min={1} max={3650}
                                                value={securityDraft.keyRotationDays}
                                                onChange={e => setSecurityDraft(d => d && ({ ...d, keyRotationDays: parseInt(e.target.value) || 90 }))}
                                                className="mt-1 block w-full sm:w-32 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                Grace period (days) <span className="text-gray-400 text-xs">— how long the old key stays in JWKS after rotation</span>
                                            </label>
                                            <input type="number" min={0} max={365}
                                                value={securityDraft.keyGracePeriodDays}
                                                onChange={e => setSecurityDraft(d => d && ({ ...d, keyGracePeriodDays: parseInt(e.target.value) ?? 14 }))}
                                                className="mt-1 block w-full sm:w-32 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Signature Provider */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Signature Provider</h3>
                                {!signatureProviderEditing ? (
                                    <button
                                        onClick={() => {
                                            const sp = (selectedTenant as any).signatureProvider ?? {};
                                            setSignatureProviderDraft({
                                                ...defaultSignatureProviderSettings,
                                                ...sp,
                                                skribble: { ...defaultSkribbleSettings, ...(sp.skribble ?? {}) },
                                                docusign: { ...defaultDocuSignSettings, ...(sp.docusign ?? {}) },
                                            });
                                            setSignatureProviderEditing(true);
                                        }}
                                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        <FiEdit2 className="mr-1.5 h-3.5 w-3.5" /> Edit
                                    </button>
                                ) : (
                                    <div className="flex gap-2">
                                        <button onClick={() => setSignatureProviderEditing(false)}
                                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                                            <FiX className="mr-1.5 h-3.5 w-3.5" /> Cancel
                                        </button>
                                        <button onClick={handleSaveSignatureProvider} disabled={signatureProviderSaving}
                                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
                                            <FiSave className="mr-1.5 h-3.5 w-3.5" /> {signatureProviderSaving ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!signatureProviderEditing ? (
                                (() => {
                                    const sp = (selectedTenant as any).signatureProvider ?? {};
                                    const provider = sp.provider ?? 'None';
                                    const sk = sp.skribble ?? {};
                                    const ds = sp.docusign ?? {};
                                    return (
                                        <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                                            <div>
                                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Provider</dt>
                                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{provider}</dd>
                                            </div>
                                            {provider === 'Skribble' && (
                                                <>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">API Username</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{sk.apiUsername || '-'}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Demo Mode</dt>
                                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{sk.isDemoMode ? 'Enabled' : 'Disabled'}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">API Key — Vault Key Name</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{sk.apiKeySecretName || '-'}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Webhook Secret — Vault Key Name</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{sk.webhookSecretName || '-'}</dd>
                                                    </div>
                                                    <div className="sm:col-span-2">
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Default Signing Message</dt>
                                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{sk.defaultMessage || '-'}</dd>
                                                    </div>
                                                </>
                                            )}
                                            {provider === 'DocuSign' && (
                                                <>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Integration Key</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{ds.integratorKey || '-'}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Impersonated User ID</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{ds.userId || '-'}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">API Account ID</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{ds.accountId || '-'}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Auth Server</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{ds.authServer || '-'}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Demo Mode</dt>
                                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white">{ds.isDemoMode ? 'Enabled' : 'Disabled'}</dd>
                                                    </div>
                                                    <div />
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">RSA Private Key — Vault Key Name</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{ds.privateKeySecretName || '-'}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Connect HMAC Key — Vault Key Name</dt>
                                                        <dd className="mt-1 text-sm font-mono text-gray-900 dark:text-white">{ds.webhookHmacKeySecretName || '-'}</dd>
                                                    </div>
                                                    <div className="sm:col-span-2">
                                                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Default Signing Message</dt>
                                                        <dd className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{ds.defaultMessage || '-'}</dd>
                                                    </div>
                                                </>
                                            )}
                                        </dl>
                                    );
                                })()
                            ) : signatureProviderDraft && (
                                <div className="space-y-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                        AppManagement stores only the Vault key NAMES. The secret values themselves — the Skribble API key and webhook secret, or the DocuSign RSA private key PEM and Connect HMAC key — must be stored in the tenant's Vault under those names, never entered here. Changes take effect after the platform tenant cache reloads.
                                    </p>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Provider</label>
                                        <select value={signatureProviderDraft.provider}
                                            onChange={e => setSignatureProviderDraft(d => d && ({ ...d, provider: e.target.value as any }))}
                                            className="mt-1 block w-full sm:w-64 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm">
                                            {SIGNATURE_PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                                        </select>
                                    </div>
                                    {signatureProviderDraft.provider === 'Skribble' && (
                                        <>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        API Username <span className="text-gray-400 text-xs">— non-secret login identifier</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.skribble?.apiUsername ?? ''}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, skribble: { ...defaultSkribbleSettings, ...d.skribble, apiUsername: e.target.value } }))}
                                                        className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                </div>
                                                <div className="flex items-center mt-6">
                                                    <input id="skribble-demo-mode" type="checkbox"
                                                        checked={signatureProviderDraft.skribble?.isDemoMode ?? false}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, skribble: { ...defaultSkribbleSettings, ...d.skribble, isDemoMode: e.target.checked } }))}
                                                        className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                                                    <label htmlFor="skribble-demo-mode" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Demo mode <span className="text-gray-400 text-xs">— use Skribble sandbox</span>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        API Key — Vault Key Name <span className="text-gray-400 text-xs">— pointer, not the key</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.skribble?.apiKeySecretName ?? ''}
                                                        placeholder={defaultSkribbleSettings.apiKeySecretName}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, skribble: { ...defaultSkribbleSettings, ...d.skribble, apiKeySecretName: e.target.value } }))}
                                                        className="mt-1 block w-full font-mono text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Webhook Secret — Vault Key Name <span className="text-gray-400 text-xs">— pointer, not the secret</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.skribble?.webhookSecretName ?? ''}
                                                        placeholder={defaultSkribbleSettings.webhookSecretName}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, skribble: { ...defaultSkribbleSettings, ...d.skribble, webhookSecretName: e.target.value } }))}
                                                        className="mt-1 block w-full font-mono text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Default signing message <span className="text-gray-400 text-xs">— shown to signers by default (optional)</span>
                                                </label>
                                                <textarea rows={3}
                                                    value={signatureProviderDraft.skribble?.defaultMessage ?? ''}
                                                    onChange={e => setSignatureProviderDraft(d => d && ({ ...d, skribble: { ...defaultSkribbleSettings, ...d.skribble, defaultMessage: e.target.value } }))}
                                                    className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                            </div>
                                        </>
                                    )}
                                    {signatureProviderDraft.provider === 'DocuSign' && (
                                        <>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                Every value below is non-secret and comes from DocuSign &rarr; Settings &rarr; Apps and Keys.
                                                The RSA private key PEM and the Connect HMAC key are seeded directly into the tenant Vault
                                                under the key names given here — there is deliberately no field for either value.
                                            </p>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Integration Key <span className="text-gray-400 text-xs">— non-secret client GUID</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.docusign?.integratorKey ?? ''}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, docusign: { ...defaultDocuSignSettings, ...d.docusign, integratorKey: e.target.value } }))}
                                                        className="mt-1 block w-full font-mono text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Impersonated User ID <span className="text-gray-400 text-xs">— the API Username GUID</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.docusign?.userId ?? ''}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, docusign: { ...defaultDocuSignSettings, ...d.docusign, userId: e.target.value } }))}
                                                        className="mt-1 block w-full font-mono text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        API Account ID <span className="text-gray-400 text-xs">— envelopes are created under it</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.docusign?.accountId ?? ''}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, docusign: { ...defaultDocuSignSettings, ...d.docusign, accountId: e.target.value } }))}
                                                        className="mt-1 block w-full font-mono text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Auth Server <span className="text-gray-400 text-xs">— host only, no scheme</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.docusign?.authServer ?? ''}
                                                        placeholder={defaultDocuSignSettings.authServer}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, docusign: { ...defaultDocuSignSettings, ...d.docusign, authServer: e.target.value } }))}
                                                        className="mt-1 block w-full font-mono text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                    <p className="mt-1 text-xs text-gray-400">account-d.docusign.com = sandbox · account.docusign.com = production</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                <input id="docusign-demo-mode" type="checkbox"
                                                    checked={signatureProviderDraft.docusign?.isDemoMode ?? false}
                                                    onChange={e => setSignatureProviderDraft(d => d && ({ ...d, docusign: { ...defaultDocuSignSettings, ...d.docusign, isDemoMode: e.target.checked } }))}
                                                    className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                                                <label htmlFor="docusign-demo-mode" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Demo mode <span className="text-gray-400 text-xs">— informational only; the REST host is resolved from the authenticated account, not from this flag</span>
                                                </label>
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        RSA Private Key — Vault Key Name <span className="text-gray-400 text-xs">— pointer, not the PEM</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.docusign?.privateKeySecretName ?? ''}
                                                        placeholder={defaultDocuSignSettings.privateKeySecretName}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, docusign: { ...defaultDocuSignSettings, ...d.docusign, privateKeySecretName: e.target.value } }))}
                                                        className="mt-1 block w-full font-mono text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        Connect HMAC Key — Vault Key Name <span className="text-gray-400 text-xs">— pointer, not the key</span>
                                                    </label>
                                                    <input type="text"
                                                        value={signatureProviderDraft.docusign?.webhookHmacKeySecretName ?? ''}
                                                        placeholder={defaultDocuSignSettings.webhookHmacKeySecretName}
                                                        onChange={e => setSignatureProviderDraft(d => d && ({ ...d, docusign: { ...defaultDocuSignSettings, ...d.docusign, webhookHmacKeySecretName: e.target.value } }))}
                                                        className="mt-1 block w-full font-mono text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                    <p className="mt-1 text-xs text-gray-400">The same HMAC secret must also be registered on the DocuSign account&apos;s Connect configuration, or every callback is rejected and the workflow never resumes.</p>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Default signing message <span className="text-gray-400 text-xs">— shown to signers by default (optional)</span>
                                                </label>
                                                <textarea rows={3}
                                                    value={signatureProviderDraft.docusign?.defaultMessage ?? ''}
                                                    onChange={e => setSignatureProviderDraft(d => d && ({ ...d, docusign: { ...defaultDocuSignSettings, ...d.docusign, defaultMessage: e.target.value } }))}
                                                    className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Login Methods */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Login Methods</h3>
                            <div className="space-y-2">
                                {(() => {
                                    const providers: any[] = selectedTenant.authenticationProviders ?? [];
                                    const kc = (selectedTenant as any).keycloak ?? {};
                                    // credentialLoginDisabled is an explicit opt-out flag; credential is ON by default.
                                    const hasCredential = !(selectedTenant as any).credentialLoginDisabled;
                                    const oidcProviders = providers.filter((p: any) => p.type === 'oidc');

                                    return (
                                        <>
                                            {/* Credential — togglable */}
                                            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                <div>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">Username / Password</span>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Standard credential login via Keycloak</p>
                                                </div>
                                                <button
                                                    disabled={loginMethodSaving}
                                                    onClick={() => handleToggleCredential(!hasCredential)}
                                                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${hasCredential ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'} disabled:opacity-50`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${hasCredential ? 'translate-x-4' : 'translate-x-0'}`} />
                                                </button>
                                            </div>

                                            {/* OIDC / AD providers — managed via KC sync, shown read-only */}
                                            {oidcProviders.map((p: any, i: number) => (
                                                <div key={i} className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{p.label ?? p.alias}</span>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Federated SSO · alias: {p.alias}</p>
                                                    </div>
                                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">Active</span>
                                                </div>
                                            ))}

                                            {/* External: OTP + Magic-link — managed via KC client sync */}
                                            {kc.clients?.externalOtp && (
                                                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">Email OTP</span>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">6-digit code by email · client: {kc.clients.externalOtp}</p>
                                                    </div>
                                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">Active</span>
                                                </div>
                                            )}
                                            {kc.clients?.externalMagicLink && (
                                                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">Magic Link</span>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">One-click email login · client: {kc.clients.externalMagicLink}</p>
                                                    </div>
                                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">Active</span>
                                                </div>
                                            )}
                                            {/* Word Add-in — gated on the live KC check, not just the stored client id,
                                                so a client that was removed/never created in Keycloak doesn't show as Active. */}
                                            {kc.clients?.wordAddin && kcStatus?.wordAddinClient === 'ok' && (
                                                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">Word Add-in</span>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Office Word Add-in login · client: {kc.clients.wordAddin}</p>
                                                    </div>
                                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300">Active</span>
                                                </div>
                                            )}
                                            {kc.clients?.wordAddin && (kcStatus?.wordAddinClient === 'missing' || kcStatus?.wordAddinClient === 'disabled') && (
                                                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">Word Add-in</span>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Office Word Add-in login · client: {kc.clients.wordAddin}</p>
                                                    </div>
                                                    <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                                                        {kcStatus?.wordAddinClient === 'disabled' ? 'Disabled' : 'Missing in Keycloak'}
                                                    </span>
                                                </div>
                                            )}

                                            {!hasCredential && oidcProviders.length === 0 && !kc.clients?.externalOtp && !kc.clients?.externalMagicLink && !kc.clients?.wordAddin && (
                                                <p className="text-sm text-gray-500 dark:text-gray-400">No login methods configured.</p>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Session Policy */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Session Policy</h3>
                                {!sessionPolicyEditing ? (
                                    <button
                                        onClick={() => {
                                            const sp = (selectedTenant as any).sessionPolicy ?? {};
                                            setSessionPolicyDraft({ ...defaultSessionPolicy, ...sp, requireReauthOnSessionExpiry: sp.requireReauthOnSessionExpiry ?? false });
                                            setKcTimeoutsDraft({
                                                ssoSessionIdleTimeoutSeconds:    sp.ssoSessionIdleTimeoutSeconds    ?? null,
                                                ssoSessionMaxLifespanSeconds:    sp.ssoSessionMaxLifespanSeconds    ?? null,
                                                accessTokenLifespanSeconds:      sp.accessTokenLifespanSeconds      ?? null,
                                                clientSessionIdleTimeoutSeconds: sp.clientSessionIdleTimeoutSeconds ?? null,
                                                clientSessionMaxLifespanSeconds: sp.clientSessionMaxLifespanSeconds ?? null,
                                            });
                                            setSessionPolicyEditing(true);
                                        }}
                                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        <FiEdit2 className="mr-1.5 h-3.5 w-3.5" /> Edit
                                    </button>
                                ) : (
                                    <div className="flex gap-2">
                                        <button onClick={() => setSessionPolicyEditing(false)}
                                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                                            <FiX className="mr-1.5 h-3.5 w-3.5" /> Cancel
                                        </button>
                                        <button onClick={handleSaveSessionPolicy} disabled={sessionPolicySaving}
                                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
                                            <FiSave className="mr-1.5 h-3.5 w-3.5" /> {sessionPolicySaving ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!sessionPolicyEditing ? (() => {
                                const sp: SessionPolicy = { ...defaultSessionPolicy, ...((selectedTenant as any).sessionPolicy ?? {}) };
                                return (
                                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                                        {[
                                            ['Concurrent Sessions', sp.allowConcurrentSessions ? (sp.maxConcurrentSessions ? `Max ${sp.maxConcurrentSessions}` : 'Unlimited') : 'Disabled'],
                                            ['Max Extensions', sp.maxExtensions != null ? String(sp.maxExtensions) : 'Unlimited'],
                                            ['Remember Me', sp.rememberMeEnabled ? `Enabled (${sp.rememberMeDurationDays}d)` : 'Disabled'],
                                            ['Default Session', `${sp.defaultSessionDurationMinutes} min`],
                                            ['IP Tracking', sp.trackIpAddress ? `Enabled (retain ${sp.ipRetentionDays}d)` : 'Disabled (GDPR opt-in)'],
                                            ['Re-auth on expiry', sp.requireReauthOnSessionExpiry ? 'Enforced — KC session killed on timeout' : 'Disabled — SSO survives'],
                                            ['SSO Idle', sp.ssoSessionIdleTimeoutSeconds != null ? `${Math.round(sp.ssoSessionIdleTimeoutSeconds / 60)} min` : 'Keycloak default'],
                                            ['SSO Max', sp.ssoSessionMaxLifespanSeconds != null ? `${Math.round(sp.ssoSessionMaxLifespanSeconds / 60)} min` : 'Keycloak default'],
                                            ['Access Token', sp.accessTokenLifespanSeconds != null ? `${Math.round(sp.accessTokenLifespanSeconds / 60)} min` : 'Keycloak default'],
                                        ].map(([label, value]) => (
                                            <div key={label}>
                                                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
                                                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{value}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                );
                            })() : sessionPolicyDraft && (
                                <div className="space-y-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                    {/* Concurrent Sessions */}
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div className="flex items-center gap-3">
                                            <input type="checkbox" id="sp-concurrent" checked={sessionPolicyDraft.allowConcurrentSessions}
                                                onChange={e => setSessionPolicyDraft(d => d && ({ ...d, allowConcurrentSessions: e.target.checked }))}
                                                className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                                            <label htmlFor="sp-concurrent" className="text-sm font-medium text-gray-700 dark:text-gray-300">Allow concurrent sessions</label>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max concurrent <span className="text-gray-400 text-xs">(blank = unlimited)</span></label>
                                            <input type="number" min={1}
                                                value={sessionPolicyDraft.maxConcurrentSessions ?? ''}
                                                onChange={e => setSessionPolicyDraft(d => d && ({ ...d, maxConcurrentSessions: e.target.value === '' ? null : parseInt(e.target.value) }))}
                                                className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                        </div>
                                    </div>

                                    {/* Max Extensions */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max session extensions <span className="text-gray-400 text-xs">(blank = unlimited)</span></label>
                                        <input type="number" min={0}
                                            value={sessionPolicyDraft.maxExtensions ?? ''}
                                            onChange={e => setSessionPolicyDraft(d => d && ({ ...d, maxExtensions: e.target.value === '' ? null : parseInt(e.target.value) }))}
                                            className="mt-1 block w-full sm:w-48 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                    </div>

                                    {/* Default session duration */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Default session duration (minutes)</label>
                                        <input type="number" min={1}
                                            value={sessionPolicyDraft.defaultSessionDurationMinutes}
                                            onChange={e => setSessionPolicyDraft(d => d && ({ ...d, defaultSessionDurationMinutes: parseInt(e.target.value) || 15 }))}
                                            className="mt-1 block w-full sm:w-48 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                    </div>

                                    {/* Remember Me */}
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div className="flex items-center gap-3">
                                            <input type="checkbox" id="sp-rememberme" checked={sessionPolicyDraft.rememberMeEnabled}
                                                onChange={e => setSessionPolicyDraft(d => d && ({ ...d, rememberMeEnabled: e.target.checked }))}
                                                className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                                            <label htmlFor="sp-rememberme" className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable "Remember Me"</label>
                                        </div>
                                        {sessionPolicyDraft.rememberMeEnabled && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Remember me duration (days)</label>
                                                <input type="number" min={1} max={365}
                                                    value={sessionPolicyDraft.rememberMeDurationDays}
                                                    onChange={e => setSessionPolicyDraft(d => d && ({ ...d, rememberMeDurationDays: parseInt(e.target.value) || 30 }))}
                                                    className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                            </div>
                                        )}
                                    </div>

                                    {/* IP Tracking (GDPR opt-in) */}
                                    <div className="border border-amber-200 dark:border-amber-700 rounded-md p-3 bg-amber-50 dark:bg-amber-900/20">
                                        <div className="flex items-start gap-3">
                                            <input type="checkbox" id="sp-ip" checked={sessionPolicyDraft.trackIpAddress}
                                                onChange={e => setSessionPolicyDraft(d => d && ({ ...d, trackIpAddress: e.target.checked }))}
                                                className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded" />
                                            <div>
                                                <label htmlFor="sp-ip" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Track IP address per session <span className="text-amber-600 dark:text-amber-400 font-semibold">(GDPR opt-in)</span>
                                                </label>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Stores the client IP with each session for security audit purposes. Justified under Art. 6(1)(f) legitimate interest.
                                                    Ensure this is documented in your ROPA and privacy notice before enabling.
                                                </p>
                                            </div>
                                        </div>
                                        {sessionPolicyDraft.trackIpAddress && (
                                            <div className="mt-3 ml-7">
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">IP data retention (days)</label>
                                                <input type="number" min={1} max={3650}
                                                    value={sessionPolicyDraft.ipRetentionDays}
                                                    onChange={e => setSessionPolicyDraft(d => d && ({ ...d, ipRetentionDays: parseInt(e.target.value) || 90 }))}
                                                    className="mt-1 block w-32 text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Re-authentication on session expiry */}
                                    <div className="border border-red-200 dark:border-red-700 rounded-md p-3 bg-red-50 dark:bg-red-900/20 mt-2">
                                        <div className="flex items-start gap-3">
                                            <input type="checkbox" id="sp-reauth"
                                                checked={sessionPolicyDraft.requireReauthOnSessionExpiry ?? false}
                                                onChange={e => setSessionPolicyDraft(d => d && ({ ...d, requireReauthOnSessionExpiry: e.target.checked }))}
                                                className="mt-0.5 h-4 w-4 text-red-600 border-gray-300 rounded" />
                                            <div>
                                                <label htmlFor="sp-reauth" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                    Force re-authentication on session expiry
                                                    <span className="ml-2 text-red-600 dark:text-red-400 font-semibold">(Recommended for GRC)</span>
                                                </label>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                    When enabled, the Keycloak SSO session is terminated on BFF session timeout.
                                                    Users must re-enter credentials — silent SSO re-login is not possible.
                                                    Disable only for tenants with multiple apps sharing the same KC realm.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Keycloak Realm Timeouts */}
                                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-2">
                                        <div className="flex items-center justify-between mb-3">
                                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Keycloak Realm Session Timeouts</p>
                                            <button onClick={handleSaveKcTimeouts} disabled={kcTimeoutsSaving}
                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                                                <FiZap className="mr-1 h-3 w-3" /> {kcTimeoutsSaving ? 'Syncing…' : 'Sync to Keycloak'}
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">All values in minutes. Leave blank to keep Keycloak realm defaults.</p>
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                            {([
                                                ['SSO Idle timeout', 'ssoSessionIdleTimeoutSeconds'],
                                                ['SSO Max lifespan', 'ssoSessionMaxLifespanSeconds'],
                                                ['Access token lifetime', 'accessTokenLifespanSeconds'],
                                                ['Client session idle', 'clientSessionIdleTimeoutSeconds'],
                                                ['Client session max', 'clientSessionMaxLifespanSeconds'],
                                            ] as [string, keyof KcSessionTimeouts][]).map(([label, key]) => (
                                                <div key={key}>
                                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label} (min)</label>
                                                    <input type="number" min={0}
                                                        value={kcTimeoutsDraft[key] != null ? Math.round((kcTimeoutsDraft[key] as number) / 60) : ''}
                                                        onChange={e => setKcTimeoutsDraft(d => ({
                                                            ...d,
                                                            [key]: e.target.value === '' ? null : parseInt(e.target.value) * 60
                                                        }))}
                                                        placeholder="KC default"
                                                        className="block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md shadow-sm" />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Admin: Force Logout */}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Force Logout</h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                Immediately revoke all active sessions for a user. The user will be signed out on their next request.
                            </p>
                            <div className="mt-4 flex gap-3 items-end">
                                <div className="flex-1 max-w-sm">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User ID</label>
                                    <input
                                        type="text"
                                        value={forceLogoutUserId}
                                        onChange={e => setForceLogoutUserId(e.target.value)}
                                        placeholder="Enter user ID…"
                                        className="mt-1 block w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm"
                                    />
                                </div>
                                <button
                                    onClick={handleForceLogout}
                                    disabled={!forceLogoutUserId.trim() || forceLogoutLoading}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <FiZap className="mr-2 h-4 w-4" />
                                    {forceLogoutLoading ? 'Logging out…' : 'Force Logout'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Sub-tenant scoping */}
            {selectedTenant.status !== 'deleted' && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Sub-tenant Scoping
                            </span>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                Off by default. When on, object types flagged <code>subtenantScoped</code> are filtered by each user's selected sub-tenants.
                                Enable only after sub-tenants exist, memberships are assigned and existing records are backfilled — otherwise untagged records disappear for non-admin users.
                            </p>
                        </div>
                        <button
                            id="subtenants-enabled"
                            role="switch"
                            aria-checked={!!selectedTenant.subtenantsEnabled}
                            aria-label="Sub-tenant scoping enabled"
                            disabled={subtenantsSaving}
                            onClick={() => handleToggleSubtenants(!selectedTenant.subtenantsEnabled)}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${selectedTenant.subtenantsEnabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'} disabled:opacity-50`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${selectedTenant.subtenantsEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                    </div>
                </div>
            )}

            {/* Dedicated Deployment Object Types */}
            {selectedTenant.status !== 'deleted' && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg px-4 py-3">
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Dedicated Deployment Object Types
                            </span>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                Object types that route to their own dedicated DataProvider pod instead of the shared instance.
                            </p>
                        </div>
                        {!dedicatedTypesEditing && (
                            <button
                                onClick={() => {
                                    setDedicatedTypesDraft(
                                        (selectedTenant as any).dedicatedDeploymentObjectTypes ?? null
                                    );
                                    setDedicatedTypesEditing(true);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                                <FiEdit2 className="h-3 w-3" /> Edit
                            </button>
                        )}
                    </div>

                    {/* Read view */}
                    {!dedicatedTypesEditing && (
                        <div className="mt-3">
                            {(selectedTenant as any).dedicatedDeploymentObjectTypes == null ? (
                                <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                                    Platform default: Contracts, Partners
                                </span>
                            ) : (selectedTenant as any).dedicatedDeploymentObjectTypes.length === 0 ? (
                                <span className="text-xs text-amber-600 dark:text-amber-400">
                                    None — all object types use the shared provider
                                </span>
                            ) : (
                                <div className="flex flex-wrap gap-1.5">
                                    {(selectedTenant as any).dedicatedDeploymentObjectTypes.map((t: string) => (
                                        <span key={t} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                            {t}
                                            {(t.toLowerCase() === 'contracts' || t.toLowerCase() === 'partners') && (
                                                <span className="ml-1 text-green-600 dark:text-green-400" title="Globally provisioned — always safe">✓</span>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Edit view */}
                    {dedicatedTypesEditing && (
                        <div className="mt-3 space-y-3">
                            {/* Infrastructure warning */}
                            <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                                <strong>Infrastructure prerequisite:</strong> "Dedicated" routing only works if a DataProvider pod with Dapr app-id <code>{'{type}'}-provider</code> is running. Adding a type here does NOT provision that pod.<br />
                                <span className="mt-1 block">Pre-provisioned (always safe): <strong>Contracts</strong>, <strong>Partners</strong>. Any other type requires infrastructure work first.</span>
                            </div>

                            {/* Chip list */}
                            <div className="flex flex-wrap gap-1.5">
                                {(dedicatedTypesDraft ?? ['Contracts', 'Partners']).map((t: string) => {
                                    const isSafe = t.toLowerCase() === 'contracts' || t.toLowerCase() === 'partners';
                                    return (
                                        <span key={t} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                            isSafe
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                                        }`}>
                                            {t}
                                            {isSafe && <span title="Globally provisioned">✓</span>}
                                            {!isSafe && <span title="Pod provisioning required" className="text-amber-500">⚠</span>}
                                            <button
                                                onClick={() => setDedicatedTypesDraft(
                                                    (dedicatedTypesDraft ?? ['Contracts', 'Partners']).filter(x => x !== t)
                                                )}
                                                className="ml-0.5 hover:text-red-600">✕</button>
                                        </span>
                                    );
                                })}
                            </div>

                            {/* Add type input */}
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={dedicatedTypesNewEntry}
                                    onChange={e => setDedicatedTypesNewEntry(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && dedicatedTypesNewEntry.trim()) {
                                            const val = dedicatedTypesNewEntry.trim();
                                            if (!(dedicatedTypesDraft ?? []).includes(val)) {
                                                setDedicatedTypesDraft([...(dedicatedTypesDraft ?? ['Contracts', 'Partners']), val]);
                                            }
                                            setDedicatedTypesNewEntry('');
                                        }
                                    }}
                                    placeholder="Add type (Pascal-case, e.g. Risks)"
                                    className="flex-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1"
                                />
                                <button
                                    onClick={() => {
                                        const val = dedicatedTypesNewEntry.trim();
                                        if (val && !(dedicatedTypesDraft ?? []).includes(val)) {
                                            setDedicatedTypesDraft([...(dedicatedTypesDraft ?? ['Contracts', 'Partners']), val]);
                                        }
                                        setDedicatedTypesNewEntry('');
                                    }}
                                    className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                                    Add
                                </button>
                            </div>

                            {/* Reset link */}
                            <button
                                onClick={() => setDedicatedTypesDraft(null)}
                                className="text-xs text-blue-500 hover:underline">
                                Reset to platform default (Contracts, Partners)
                            </button>

                            {/* Two-store sync note */}
                            <p className="text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700 pt-2 mt-2">
                                This value is stored in AppManagement. To apply it to running platform services,
                                also update <code>TenantsSettings.DedicatedDeploymentObjectTypes</code> in Azure App Config / Consul
                                for this tenant and restart DataProvider, Authorization, and Workflow.
                            </p>

                            {/* Save / Cancel */}
                            <div className="flex items-center gap-2 pt-1">
                                <button
                                    onClick={handleSaveDedicatedTypes}
                                    disabled={dedicatedTypesSaving}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                                    {dedicatedTypesSaving
                                        ? <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                                        : <FiSave className="h-3 w-3" />}
                                    Save
                                </button>
                                <button
                                    onClick={() => { setDedicatedTypesEditing(false); setDedicatedTypesDraft(null); setDedicatedTypesNewEntry(''); }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <FiX className="h-3 w-3" /> Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TenantDetailsPage;